import type * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'

import { cn } from '@/helpers/utils'

function Drawer({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
	return (
		// iOS Safari: vaul's scroll handling jumps the page every time the drawer
		// closes. It runs two systems there — the body `position: fixed`
		// scroll-lock (usePositionFixed) whose restore mis-lands on close, and an
		// input-repositioning helper (usePreventScroll) that does scrollTo(0,0) on
		// open. `noBodyStyles` disables the former, `repositionInputs={false}` the
		// latter, so vaul no longer touches the scroll position. The modal overlay
		// still blocks interaction with the background. Overridable via props.
		<DrawerPrimitive.Root
			data-slot='drawer'
			noBodyStyles
			repositionInputs={false}
			{...props}
		/>
	)
}

function DrawerNestedRoot({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.NestedRoot>) {
	return (
		<DrawerPrimitive.NestedRoot data-slot='drawer-nested-root' {...props} />
	)
}

function DrawerTrigger({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
	return <DrawerPrimitive.Trigger data-slot='drawer-trigger' {...props} />
}

function DrawerPortal({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
	return <DrawerPrimitive.Portal data-slot='drawer-portal' {...props} />
}

function DrawerClose({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
	return <DrawerPrimitive.Close data-slot='drawer-close' {...props} />
}

function DrawerOverlay({
	className,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
	return (
		<DrawerPrimitive.Overlay
			className={cn(
				'data-open:fade-in-0 data-closed:fade-out-0 fixed inset-0 z-(--z-modal-overlay) bg-neutral-black/10 data-closed:animate-out data-open:animate-in supports-backdrop-filter:backdrop-blur-xs',
				className
			)}
			data-slot='drawer-overlay'
			{...props}
		/>
	)
}

function DrawerContent({
	className,
	children,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Content>) {
	return (
		<DrawerPortal data-slot='drawer-portal'>
			<DrawerOverlay />
			<DrawerPrimitive.Content
				className={cn(
					'group/drawer-content fixed z-(--z-modal) flex h-auto flex-col bg-popover text-popover-foreground text-sm data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=left]:s:max-w-sm data-[vaul-drawer-direction=right]:s:max-w-sm data-[vaul-drawer-direction=bottom]:rounded-t-xl data-[vaul-drawer-direction=left]:rounded-r-xl data-[vaul-drawer-direction=top]:rounded-b-xl data-[vaul-drawer-direction=right]:rounded-l-xl data-[vaul-drawer-direction=bottom]:border-t data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=top]:border-b data-[vaul-drawer-direction=right]:border-l',
					className
				)}
				data-slot='drawer-content'
				{...props}
			>
				<div className='mx-auto mt-4 hidden h-1.5 w-[100px] shrink-0 rounded-full bg-muted group-data-[vaul-drawer-direction=bottom]/drawer-content:block' />
				{children}
			</DrawerPrimitive.Content>
		</DrawerPortal>
	)
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'flex flex-col gap-0.5 m:gap-1.5 p-4 m:text-left group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center',
				className
			)}
			data-slot='drawer-header'
			{...props}
		/>
	)
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn('mt-auto flex flex-col gap-2 p-4', className)}
			data-slot='drawer-footer'
			{...props}
		/>
	)
}

function DrawerTitle({
	className,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
	return (
		<DrawerPrimitive.Title
			className={cn('font-medium text-foreground', className)}
			data-slot='drawer-title'
			{...props}
		/>
	)
}

function DrawerDescription({
	className,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
	return (
		<DrawerPrimitive.Description
			className={cn('text-muted-foreground text-sm', className)}
			data-slot='drawer-description'
			{...props}
		/>
	)
}

export {
	Drawer,
	DrawerNestedRoot,
	DrawerPortal,
	DrawerOverlay,
	DrawerTrigger,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerFooter,
	DrawerTitle,
	DrawerDescription
}
